import { Lines, PageHeading, Person, SkeletonBlock } from './SkeletonPrimitives';
import './communitySkeletons.css';

function Bar({
  className = '',
  width,
  height = 12,
}: {
  className?: string;
  width?: number | string;
  height?: number;
}) {
  return <SkeletonBlock className={className} style={{ width, height }} />;
}

function PersonShape() {
  return (
    <div className="sk-community-person">
      <Person />
    </div>
  );
}

function Tools({ trade = false }: { trade?: boolean }) {
  return (
    <div className={trade ? 'c-trade-toolbar' : 'c-toolbar sk-community-toolbar'}>
      <Bar className="sk-community-input c-toolbar-search" height={33} />
      {trade && <Bar className="sk-community-input" height={33} />}
      <Bar className="sk-community-filter" height={33} />
      {trade && <Bar className="sk-community-filter" height={33} />}
    </div>
  );
}

function Tabs() {
  return (
    <div className="sk-community-tabs">
      <Bar width={65} />
      <Bar width={78} />
      <Bar className="sk-community-filter" width={134} height={32} />
    </div>
  );
}

/** Same post shell as PostCard; its parent owns the feed/profile column width. */
export function PostSkeleton({ count = 3 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div className="c-post c-panel sk-community-post" key={index}>
          <div className="c-post-head">
            <PersonShape />
            <Bar width={18} height={6} />
          </div>
          <div className="sk-community-post-copy">
            <Lines />
            <Bar width="57%" />
          </div>
          {index % 3 === 0 && <SkeletonBlock className="sk-community-post-media" />}
          <div className="sk-community-post-actions">
            <Bar width={65} height={12} />
            <Bar width={72} height={12} />
            <Bar width={55} height={12} />
          </div>
        </div>
      ))}
    </>
  );
}

function StoryTray() {
  return (
    <div className="c-story-tray sk-community-story-tray">
      {Array.from({ length: 6 }, (_, index) => (
        <div className="c-story-item" key={index}>
          <SkeletonBlock className="sk-avatar sk-community-story-avatar" />
          <Bar width={48} height={10} />
        </div>
      ))}
    </div>
  );
}

function Home({ count }: { count: number }) {
  return (
    <>
      <div className="gym-hero c-home-feature sk-community-home-cover">
        <div className="gym-hero-overlay">
          <Bar width={160} height={12} />
          <SkeletonBlock className="sk-community-gym-name" />
          <Bar width={180} />
          <Bar width={108} height={12} />
        </div>
      </div>
      <div className="c-main-columns">
        <div className="c-feed-column">
          <StoryTray />
          <Tabs />
          <div className="c-feed-grid">
            <PostSkeleton count={count} />
          </div>
        </div>
        <aside className="c-right-column sk-community-aside">
          <div className="c-panel c-aside-section sk-community-aside-card">
            <Bar width={110} height={10} />
            <Bar width="78%" height={32} />
            <Lines />
            <Bar width={120} height={12} />
          </div>
          <div className="c-panel c-aside-section sk-community-aside-card">
            <Bar width={145} height={12} />
            <Inbox count={10} compact />
          </div>
          <div className="c-panel c-aside-section sk-community-aside-card">
            <Bar width={150} height={12} />
            <ForumPreview count={10} />
          </div>
        </aside>
      </div>
    </>
  );
}

function Members({ count }: { count: number }) {
  return (
    <div className="c-member-grid">
      {Array.from({ length: count }, (_, index) => (
        <div className="c-member-card c-panel sk-community-member" key={index}>
          <SkeletonBlock className="c-member-cover" />
          <div className="c-member-card-content sk-community-member-copy">
            <div className="c-member-identity">
              <SkeletonBlock className="sk-avatar sk-community-member-avatar" />
              <div className="c-member-identity-copy">
                <Bar className="sk-community-member-name" width="65%" height={15} />
                <Bar className="sk-community-member-handle" width="48%" height={10} />
              </div>
            </div>
            <div className="sk-community-member-bio">
              <Lines />
              <Bar width="57%" height={10} />
            </div>
            <div className="c-member-actions">
              <Bar className="sk-community-member-action" height={32} />
              <Bar className="sk-community-member-action" height={32} />
            </div>
            <div className="c-member-utility">
              <Bar className="sk-community-member-location" width="40%" height={11} />
              <div className="c-member-utility-actions">
                <Bar width={32} height={10} />
                <Bar width={32} height={10} />
                <Bar width={38} height={10} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Stories({ count }: { count: number }) {
  return (
    <div className="c-story-grid">
      {Array.from({ length: count }, (_, index) => (
        <div className="c-story-card sk-community-story" key={index}>
          <SkeletonBlock className="sk-community-story-image" />
          <div className="c-story-card-top">
            <SkeletonBlock className="sk-avatar sk-community-small-avatar" />
            <Bar width={42} height={9} />
          </div>
          <div className="c-story-card-body sk-community-story-copy">
            <Bar width="85%" height={17} />
            <Bar width="62%" height={17} />
            <Bar width="52%" height={10} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Topics({ count }: { count: number }) {
  return (
    <div className="sk-community-topics">
      {Array.from({ length: count }, (_, index) => (
        <div className="c-topic-row" key={index}>
          <SkeletonBlock className="sk-avatar sk-community-topic-avatar" />
          <div className="sk-community-topic-copy">
            <Bar width={92} height={8} />
            <Bar className="sk-community-topic-title" height={20} />
            <Bar width="47%" height={10} />
          </div>
          <div className="c-topic-reply-count">
            <Bar width={24} height={18} />
            <Bar width={32} height={9} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Forum({ count }: { count: number }) {
  return (
    <>
      <PageHeading action />
      <div className="c-forum-layout">
        <aside className="c-forum-categories sk-community-categories">
          {Array.from({ length: 6 }, (_, index) => (
            <div className="sk-community-category" key={index}>
              <Bar width="75%" height={13} />
              <Bar width="90%" height={10} />
            </div>
          ))}
        </aside>
        <div>
          <Tools />
          <div className="c-topic-list c-panel">
            <Topics count={count} />
          </div>
        </div>
      </div>
    </>
  );
}

function Discussion({ count }: { count: number }) {
  return (
    <>
      <Bar className="sk-community-back" width={132} />
      <div className="c-topic-detail c-panel sk-community-discussion">
        <Bar width={100} height={10} />
        <Bar className="sk-community-discussion-title" height={34} />
        <PersonShape />
        <div className="sk-community-discussion-body">
          <Lines />
          <Lines />
        </div>
        <div className="sk-chips">
          <Bar />
          <Bar />
        </div>
        <div className="c-topic-footer">
          <Bar width={145} height={32} />
          <Bar width={40} />
        </div>
      </div>
      <div className="c-reply-section sk-community-replies">
        <Bar className="sk-community-reply-heading" width={100} height={20} />
        {Array.from({ length: Math.min(count, 3) }, (_, index) => (
          <div className="c-panel c-forum-reply sk-community-reply" key={index}>
            <PersonShape />
            <Lines />
          </div>
        ))}
      </div>
    </>
  );
}

function Trades({ count }: { count: number }) {
  return (
    <div className="c-trade-grid">
      {Array.from({ length: count }, (_, index) => (
        <div className="c-listing-card c-panel sk-community-trade" key={index}>
          <SkeletonBlock className="sk-community-trade-visual" />
          <div className="c-listing-card-body sk-community-trade-copy">
            <Bar width="55%" height={8} />
            <Bar width="84%" height={20} />
            <Bar width="51%" height={10} />
            <div className="c-listing-desire sk-community-trade-desire">
              <Bar width={16} height={16} />
              <Lines short />
            </div>
            <div className="c-listing-card-footer">
              <SkeletonBlock className="sk-avatar sk-community-trade-avatar" />
              <Bar width="38%" height={10} />
              <Bar width={46} height={8} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TradeDetail() {
  return (
    <>
      <Bar className="sk-community-back" width={120} />
      <div className="c-listing-detail c-panel sk-community-trade-detail">
        <SkeletonBlock className="sk-community-trade-detail-visual" />
        <div className="c-listing-detail-content sk-community-trade-detail-copy">
          <Bar width={80} height={22} />
          <Bar width="84%" height={32} />
          <Lines />
          <Lines />
          <div className="c-trade-for">
            <Bar width={70} height={8} />
            <Bar width="72%" height={16} />
          </div>
          <PersonShape />
          <Bar width={150} height={32} />
        </div>
      </div>
    </>
  );
}

function Inbox({ count, compact = false }: { count: number; compact?: boolean }) {
  return (
    <div className={`sk-community-inbox ${compact ? 'sk-community-inbox-compact' : ''}`}>
      {Array.from({ length: count }, (_, index) => (
        <div className="c-conversation-row sk-community-inbox-row" key={index}>
          <SkeletonBlock className="sk-avatar" />
          <div className="sk-community-inbox-copy">
            <Bar width="65%" height={11} />
            <Bar width="90%" height={9} />
          </div>
          <Bar width={26} height={9} />
        </div>
      ))}
    </div>
  );
}

function History({ count }: { count: number }) {
  return (
    <div className="sk-community-history">
      {Array.from({ length: count }, (_, index) => (
        <div className={`c-message ${index % 2 ? 'c-message-mine' : ''}`} key={index}>
          {index % 2 === 0 && <SkeletonBlock className="sk-avatar sk-community-small-avatar" />}
          <div className="sk-community-message-copy">
            <div className="sk-community-bubble">
              <Lines short />
            </div>
            <Bar className="sk-community-message-time" width={62} height={8} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Conversation({ count, header = true }: { count: number; header?: boolean }) {
  return (
    <div className="sk-community-conversation">
      {header && (
        <div className="c-message-header">
          <PersonShape />
          <Bar width={22} />
        </div>
      )}
      <div className="c-message-scroll">
        <History count={count} />
      </div>
      <div className="c-message-composer">
        <Bar className="sk-community-input" height={60} />
      </div>
    </div>
  );
}

function Messages({ count, selected = false }: { count: number; selected?: boolean }) {
  return (
    <div
      className={`c-messaging c-panel sk-community-messaging ${selected ? 'c-conversation-open' : ''}`}
    >
      <aside className="c-conversation-sidebar">
        <div className="c-message-search">
          <Bar className="sk-community-input" height={38} />
        </div>
        <div className="c-tabs c-message-tabs sk-community-inbox-tabs">
          <Bar width={38} />
          <Bar width={58} />
        </div>
        <div className="c-conversation-list">
          <Inbox count={count} />
        </div>
      </aside>
      <div className="c-message-detail">
        {selected ? (
          <Conversation count={count} />
        ) : (
          <div className="sk-community-empty-conversation">
            <Bar width={40} height={40} />
            <Bar width={220} height={24} />
            <Bar width={260} />
          </div>
        )}
      </div>
    </div>
  );
}

function Notifications({ count }: { count: number }) {
  return (
    <div className="c-notification-list c-panel">
      {Array.from({ length: count }, (_, index) => (
        <div className="c-notification" key={index}>
          <SkeletonBlock className="sk-community-notice-icon" />
          <div className="sk-community-notice-copy">
            <Bar width="62%" height={12} />
            <Bar width="90%" height={11} />
            <Bar width={88} height={9} />
          </div>
          <Bar width={8} height={8} />
        </div>
      ))}
    </div>
  );
}

function ForumPreview({ count }: { count: number }) {
  return (
    <div className="c-forum-preview-list sk-community-forum-preview-list">
      {Array.from({ length: count }, (_, index) => (
        <div className="c-mini-topic" key={index}>
          <Bar width="88%" height={12} />
          <Bar width="48%" height={9} />
        </div>
      ))}
    </div>
  );
}

function Comments({ count }: { count: number }) {
  return (
    <div className="sk-community-comments">
      {Array.from({ length: count }, (_, index) => (
        <div className="c-comment" key={index}>
          <SkeletonBlock className="sk-avatar sk-community-small-avatar" />
          <div className="sk-community-comment-copy">
            <Bar width={115} height={10} />
            <Bar width="90%" height={11} />
            <Bar width="60%" height={11} />
            <Bar width={68} height={9} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CommunitySkeleton({
  variant,
  count,
  page = false,
}: {
  variant: string;
  count: number;
  page?: boolean;
}) {
  switch (variant) {
    case 'feed':
      return page ? <Home count={count} /> : <PostSkeleton count={count} />;
    case 'stories':
      return (
        <>
          {page && <PageHeading action />}
          <Stories count={count} />
        </>
      );
    case 'members':
      return (
        <>
          {page && (
            <>
              <PageHeading />
              <Tools />
              <Bar className="sk-community-result-count" width={210} height={11} />
            </>
          )}
          <Members count={count} />
        </>
      );
    case 'forum':
      return page ? <Forum count={count} /> : <Topics count={count} />;
    case 'discussion':
      return <Discussion count={count} />;
    case 'trade':
      return (
        <>
          {page && (
            <>
              <PageHeading action />
              <div className="c-trade-notice sk-community-trade-notice">
                <Bar className="c-trade-notice-icon" width={74} height={74} />
                <div>
                  <Lines />
                </div>
              </div>
              <Tools trade />
              <Bar className="sk-community-result-count" width={150} height={11} />
            </>
          )}
          <Trades count={count} />
        </>
      );
    case 'trade-detail':
      return <TradeDetail />;
    case 'messages':
      return (
        <>
          {page && <PageHeading action />}
          <Messages count={count} />
        </>
      );
    case 'conversation':
      return page ? (
        <>
          <PageHeading action />
          <Messages count={count} selected />
        </>
      ) : (
        <Conversation count={count} header={false} />
      );
    case 'inbox':
      return <Inbox count={count} />;
    case 'message-history':
      return <History count={count} />;
    case 'message-people':
      return (
        <div className="c-new-message-list">
          {Array.from({ length: count }, (_, index) => (
            <div className="sk-community-new-message-person" key={index}>
              <SkeletonBlock className="sk-avatar" />
              <Lines short />
              <Bar width={16} height={16} />
            </div>
          ))}
        </div>
      );
    case 'notifications':
      return page ? (
        <>
          <PageHeading action />
          <div className="c-notification-layout">
            <div>
              <Tabs />
              <Notifications count={count} />
            </div>
            <div className="c-panel c-notification-aside sk-community-aside-card">
              <Bar width={30} height={30} />
              <Bar width="80%" height={24} />
              <Lines />
              <Bar width={155} height={32} />
            </div>
          </div>
        </>
      ) : (
        <Notifications count={count} />
      );
    case 'forum-preview':
      return <ForumPreview count={count} />;
    case 'comments':
      return <Comments count={count} />;
    default:
      return null;
  }
}
